import * as express from 'express';
import { BaseService } from '../base';
import { Buffer } from 'buffer';
import {
    // IRemoteRequest,
    addonSerialize,
    addonDeserialize,
    log,
    UserToken,
    IRemoteClientAdapter,
    addon,
    BaseAddon,
    newError,
    fetchAddonProtoType,
    JsonRpc2Request,
    JsonRpc2Response
} from 'pao-aop';
import { Session } from '../session';
import { post, CoreOptions } from 'request';

/**
 * 通过名称获取服务的函数
 */
export type GetServiceByNameFunc = (serviceName: string) => BaseService | undefined;

/**
 * jsonRcp服务句柄
 * @param getServiceByName 根据名称获取服务的方法
 */
export function jsonRpcServiceHandler(getServiceByName: GetServiceByNameFunc) {
    return (req: express.Request, res: express.Response) => {
        // 定义数组变量,接收请求的二进制数据
        // REMARK:用二进制接收数据避免中文乱码
        let buffer: any = [];

        // 通过req的data事件监听函数，每当接受到请求体的数据，就累加到post变量中
        req.on('data', function (chunk: string | Buffer) {
            log('remote', `数据到达,url=${req.url}`);
            buffer.push(chunk);
        });

        let errorFunc = (requestID: number, error: Error) => {
            // 此处是在主线程运行，要确保不崩溃
            try {
                log('remote', new Error(`数据响应时发生未知异常: ${error}`));
                let status = (<any>error).status;
                if (!status) {
                    status = 600;
                }

                let responseObj: JsonRpc2Response = {
                    jsonrpc: '2.0',
                    result: null,
                    id: requestID,
                    error: {
                        code: status,
                        message: error.message,
                        data: null
                    }
                };

                res.send(responseObj);
                log('remote', `异常发送,url=${req.url}, error=${error}`);
            } catch (exeError) {
                log('remote', new Error(`异常处理时发生异常: ${exeError}`));
            }
        };

        let responseFunc = (requestID: number, values: any) => {
            // 此处是在主线程运行，要确保不崩溃
            try {
                fetchAddonProtoType(values);
                let responseObj: JsonRpc2Response = {
                    jsonrpc: '2.0',
                    result: values,
                    id: requestID,
                    error: null
                };
                res.send(responseObj);
                log('remote', `响应发送,url=${req.url},id=${requestID}`);
            } catch (error) {
                log('remote', new Error(`数据响应时发生未知异常: ${error}`));
            }
        };

        // 在end事件触发后，通过querystring.parse将post解析为真正的POST请求格式，然后向客户端返回。
        req.on('end', async function () {
            let requestObj: JsonRpc2Request;
            try {
                // REMARK:将请求的二进制数据进行utf8编码处理
                let data = Buffer.concat(buffer);
                let post = data.toString('utf8');
                requestObj = addonDeserialize(post);

                log('remote', `请求接收,url=${req.url},id=${requestObj.id},methodName=${requestObj.method}`);
                let session = new Session(req);

                // 此处应该设置当前用户
                // 根据名称查找服务
                let methodParts = requestObj.method.split('.');

                let serviceName = methodParts[0];   // 去掉路径前面的/
                let service = getServiceByName(serviceName);
                if (!service) {
                    throw new Error(`远程调用服务${serviceName}异常，服务找不到。`);
                }

                let methodName = methodParts[1];
                let serviceFunc = service![methodName];
                if (!serviceFunc) {
                    throw new Error(`远程调用服务${serviceName}异常: 方法${methodName}找不到。`);
                }

                // 添加当前请求对象到方法
                requestObj.params[serviceFunc.length] = req;
                // apply 调用的 caller 为当前的 service 对象
                let result = service!.call!(new UserToken(), session, methodName!, requestObj.params);
                if (result &&
                    result.constructor &&
                    result.constructor.prototype.then) {

                    // 此处是异步返回，必须try-catch，否则有崩溃风险
                    let values = await result;

                    responseFunc(requestObj.id!, values);
                } else {
                    // 处理非Promise
                    responseFunc(requestObj.id!, result);
                }
            } catch (error) {
                errorFunc(requestObj ? requestObj.id! : -1, error);
            }
        });
    };
}

/**
 * Post请求
 * @param serverAddress 服务地址
 * @param methodName jsonrpc方法名称
 * @param args 参数
 * @returns 返回参数
 */
export function postRequest(serverAddress: string, requestData: any): Promise<any> {
    let today = new Date();
    let id = today.getTime();

    fetchAddonProtoType(requestData);
    let headers = {
        "Content-Type": 'application/json'
    };

    let options: CoreOptions = {
        headers: headers,
        body: requestData
    };
    console.log("options=>", options);
    return new Promise((executor, reject) => {
        try {
            post(serverAddress, options, (error, response, body) => {

                if (error) {
                    reject(error);
                } else {
                    if (response.statusCode === 200) {
                        executor(body);
                    } else {
                        reject(newError(response.statusCode, response.statusMessage));
                    }
                }
            }).on('error', (err) => {
                reject(err);
            });
        } catch (error) {
            reject(error);
        }

    });
}

/**
 * HTTP POST 远程客户端适配器
 * @author pao
 */
@addon('HttpPostClientAdapter', 'HTTP POST 远程客户端适配器', '基于HttpPost实现的远程调用客户端')
export class HttpPostClientAdapter extends BaseAddon implements IRemoteClientAdapter {
    /**
     * 远程调用
     * @param url url地址
     * @param data 数据
     * @param success 成功执行的回调函数
     * @param error 异常执行的回调函数
     */
    remoteCall(
        url: string,
        data: string): Promise<any> {
        return postRequest(`${url}`, data);
    }
}